import { useMemo } from 'react';
import { TradeFormData, TradeSide } from '@/components/common/SidePanels/TradeSidePanel/schema';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import { NumberNoNaN, decimalRoundUp } from '@fintronners/react-utils/src/numberUtilsTSX';
import { OrderType, QuantityType } from '@fintronners/internal-core/src/trade/constants';

interface UseTradeBreakdownOptions {
  side: TradeSide;
  currentAssetPrice?: number;
  dollarBalance?: number;
  shareBalance?: number;
  formData: TradeFormData;
}

export interface TradeBreakdown {
  quantityType: QuantityType;
  shareAmount: number;
  dollarAmount: number;
  shareBalance: number;
  dollarBalance: number;
  dollarInputInSharesAtLimitPrice: number;
  shareBalanceInDollarsAtLimitPrice: number;
  shareBalanceInDollars: number;
  tradableUnits: number;
  tradableInCash: number;
  grossOrderAmount: number;
  netOrderAmount: number;
  feeAmount: number | undefined;
  limitPrice: number;
  sellingPercentage: number;
  isSellingAll: boolean;
}

/**
 * Custom hook that compute and provides detailed trade information.
 */
export const useTradeBreakdown = ({
  side,
  formData,
  shareBalance = 0,
  dollarBalance = 0,
  currentAssetPrice = 0,
}: UseTradeBreakdownOptions): TradeBreakdown => {
  const { data: previewTradeFee } = usePreviewTradeFeeStore();

  const {
    orderType,
    quantityType: rawQuantityType,
    amount: rawAmount,
    shares: rawShares,
    stopLoss: rawStopLoss,
    limitPrice: rawLimitPrice,
  } = formData;

  const quantityType =
    rawQuantityType || (rawShares !== '0' ? QuantityType.SHARES : QuantityType.DOLLARS);

  const limitPrice = NumberNoNaN(rawStopLoss) || NumberNoNaN(rawLimitPrice);
  const dollarInput = NumberNoNaN(rawAmount);
  const dollarInputInShares = decimalRoundUp(dollarInput / (currentAssetPrice || 1));
  const dollarInputInSharesAtLimitPrice = decimalRoundUp(dollarInput / (limitPrice || 1));
  const shareInput = NumberNoNaN(rawShares);
  const shareInputInDollars = decimalRoundUp(shareInput * currentAssetPrice, 2);
  const shareBalanceInDollars = decimalRoundUp(shareBalance * currentAssetPrice, 2);
  const shareBalanceInDollarsAtLimitPrice = decimalRoundUp(shareBalance * limitPrice, 2);
  const feeAmount = NumberNoNaN(previewTradeFee?.feeAmount);

  const dollarAmount = useMemo(() => {
    if (quantityType === QuantityType.DOLLARS) {
      return dollarInput;
    }

    return shareInputInDollars;
  }, [quantityType, dollarInput, shareInputInDollars]);

  const shareAmount = useMemo(() => {
    if (quantityType === QuantityType.SHARES) {
      return shareInput;
    }

    if (orderType === OrderType.STOP_LOSS || orderType === OrderType.LIMIT_BUY) {
      return dollarInputInSharesAtLimitPrice;
    }

    return dollarInputInShares;
  }, [orderType, quantityType, shareInput, dollarInputInShares, dollarInputInSharesAtLimitPrice]);

  const isSellingAll = useMemo(() => {
    if (side === TradeSide.BUY) {
      return false;
    }

    if (quantityType === QuantityType.DOLLARS) {
      if (orderType === OrderType.STOP_LOSS) {
        return dollarAmount === shareBalanceInDollarsAtLimitPrice;
      }

      return dollarAmount === shareBalanceInDollars;
    }

    return shareAmount === shareBalance;
  }, [
    side,
    orderType,
    quantityType,
    dollarAmount,
    shareAmount,
    shareBalance,
    shareBalanceInDollars,
    shareBalanceInDollarsAtLimitPrice,
  ]);

  const grossOrderAmount = dollarAmount;
  // For BUY: user pays fee with their balance
  // For SELL: user receives amount minus fee
  const netOrderAmount =
    grossOrderAmount > 0 ? grossOrderAmount + (side === TradeSide.BUY ? feeAmount : -feeAmount) : 0;

  const sellingPercentage = shareBalanceInDollars
    ? (grossOrderAmount / shareBalanceInDollars) * 100
    : 0;

  const tradableUnits = side === TradeSide.BUY ? dollarBalance : shareBalance;
  const tradableInCash = side === TradeSide.BUY ? dollarBalance : shareBalanceInDollars;

  return {
    quantityType,
    shareAmount,
    dollarAmount,
    shareBalance,
    dollarBalance,
    dollarInputInSharesAtLimitPrice,
    shareBalanceInDollarsAtLimitPrice,
    shareBalanceInDollars,
    tradableUnits,
    tradableInCash,
    grossOrderAmount,
    netOrderAmount,
    feeAmount,
    limitPrice,
    sellingPercentage,
    isSellingAll,
  };
};
